import { useLayoutEffect, useState } from "react";

/** Matches MUI default `sm` breakpoint — used to widen Recharts plot on narrow viewports */
const COMPACT_MEDIA_QUERY = "(max-width: 599.95px)";

/**
 * Pre-`addEventListener("change")` MediaQueryList (e.g. older Safari).
 * Typed narrowly so we do not call deprecated `MediaQueryList#addListener` / `#removeListener` typings.
 */
type LegacyMatchMedia = {
  addListener?: (listener: () => void) => void;
  removeListener?: (listener: () => void) => void;
};

/**
 * True when viewport is below `sm` — tighter LineChart margins so the SVG plot uses more width.
 * Implemented with matchMedia + useLayoutEffect to avoid MUI useMediaQuery + jsdom quirks in tests.
 */
export function useTrendChartCompactViewport(): boolean {
  const [compact, setCompact] = useState(false);

  useLayoutEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }
    const mq = window.matchMedia(COMPACT_MEDIA_QUERY);
    const sync = () => setCompact(mq?.matches === true);
    sync();
    if (!mq) {
      return;
    }
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", sync);
      return () => mq.removeEventListener("change", sync);
    }
    const legacy = mq as unknown as LegacyMatchMedia;
    if (typeof legacy.addListener === "function") {
      legacy.addListener(sync);
      return () => {
        legacy.removeListener?.(sync);
      };
    }
  }, []);

  return compact;
}
